import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { useUser } from "@/features/auth";
import { useCartModel } from "@/features/cart";
import { useCartSheet } from "@/features/cart";
import { useBalance, useCreateDepositDialog } from "@/features/deposits";
import { useNotificationsSheet } from "@/features/notifications";
import { useStore, useStoreBalance } from "@/features/store-dashboard";
import { useTicketsUnreadCount } from "@/features/tickets";
import { useWithdrawDialog } from "@/features/withdrawals";
import { rqClient } from "@/shared/api/instance";
import { useLocalStorage } from "@/shared/lib/use-localstorage";
import { ROUTES } from "@/shared/model/routes";
import { useSession } from "@/shared/model/session";

export type NavItemType =
  | "catalog"
  | "cart"
  | "notifications"
  | "tickets"
  | "user";

export type NavItem = {
  type: NavItemType;
  path: string;
  labelKey: string;
  badgeCount?: number;
};

const getInitials = (value?: string) => {
  if (!value) return "U";
  const trimmed = value.trim();
  if (!trimmed) return "U";
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
};

export type UseNavigationModel = ReturnType<typeof useNavigationModel>;

export const useNavigationModel = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated, logout } = useSession();
  const { user } = useUser();
  const unreadTicketsCount = useTicketsUnreadCount();
  const { data: unreadNotificationsData } = rqClient.useQuery(
    "get",
    "/api/v1/notifications/unread_count",
    {},
    { enabled: isAuthenticated },
  );
  const unreadNotificationsCount = unreadNotificationsData?.count ?? 0;
  const { balance: balanceData, isPending: isBalanceLoading } = useBalance();
  const { data: storeBalanceData, isPending: isStoreBalanceLoading } =
    useStoreBalance();
  const { data: storeData, isPending: isStoreLoading } = useStore();
  const { totals: cartTotal } = useCartModel();

  const cartSheet = useCartSheet();
  const notificationsSheet = useNotificationsSheet();
  const createDepositDialog = useCreateDepositDialog();
  const withdrawDialog = useWithdrawDialog();

  // Sheet/Drawer state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [isStoreOpen, setIsStoreOpen] = useState(false);
  const [accountDrawerView, setAccountDrawerView] = useLocalStorage<
    "user" | "store"
  >("nav-account-drawer-view", "user");

  // Nav bar items
  const navItems: NavItem[] = [
    {
      type: "catalog",
      path: ROUTES.PRODUCTS,
      labelKey: "nav.catalog",
    },
    {
      type: "cart",
      path: "#cart",
      labelKey: "nav.cart",
      badgeCount: cartTotal?.productCount ?? 0,
    },
    {
      type: "notifications",
      path: "#notifications",
      labelKey: "nav.notifications",
      badgeCount: unreadNotificationsCount,
    },
    {
      type: "tickets",
      path: ROUTES.TICKETS,
      labelKey: "nav.tickets",
      badgeCount: unreadTicketsCount,
    },
    {
      type: "user",
      path: "#user",
      labelKey: "nav.account",
    },
  ];

  const isActive = (path: string) => {
    if (path === ROUTES.PRODUCTS) {
      return (
        location.pathname === ROUTES.PRODUCTS ||
        location.pathname.startsWith(ROUTES.PRODUCTS + "/")
      );
    }
    if (path === ROUTES.TICKETS) {
      return (
        location.pathname === ROUTES.TICKETS ||
        location.pathname.startsWith(ROUTES.TICKETS + "/")
      );
    }
    return location.pathname === path;
  };

  const handleNavClick = (item: NavItem) => {
    switch (item.type) {
      case "cart":
        cartSheet.open();
        break;
      case "notifications":
        notificationsSheet.open();
        break;
      case "user":
        setIsAccountMenuOpen(true);
        break;
      default:
        navigate(item.path);
    }
  };

  const accountInitials = getInitials(user?.username || user?.email);
  const username = user?.username || user?.email || "";
  const role = user?.role || "buyer";
  const balance = balanceData ?? null;
  const storeBalance = role === "seller" ? storeBalanceData : null;
  const store = role === "seller" ? (storeData ?? null) : null;

  // Loading state - only show skeleton when authenticated and loading critical data
  const isPending = isAuthenticated
    ? isBalanceLoading ||
      (role === "seller" && (isStoreBalanceLoading || isStoreLoading))
    : false;

  // Logout handler для использования в onClick
  const handleLogout = () => logout();

  return {
    // Auth
    isAuthenticated,
    logout: handleLogout,
    user,

    // Translation
    t,

    // User data
    accountInitials,
    username,
    role,
    balance,
    storeBalance,
    store,

    // Nav items
    navItems,
    isActive,
    handleNavClick,

    // State
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    isAccountMenuOpen,
    setIsAccountMenuOpen,
    accountDrawerView,
    setAccountDrawerView,
    isNotificationsOpen: notificationsSheet.isOpen,
    setIsNotificationsOpen: notificationsSheet.setIsOpen,
    isCartOpen: cartSheet.isOpen,
    setIsCartOpen: cartSheet.setIsOpen,
    isStoreOpen,
    setIsStoreOpen,

    // Badge counts
    unreadNotificationsCount,
    unreadTicketsCount,
    cartItemCount: cartTotal?.productCount ?? 0,

    // Loading state
    isPending,

    // Dialog actions
    openDepositDialog: createDepositDialog.open,
    openWithdrawDialog: withdrawDialog.open,
  };
};
